import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const SignaturePad = ({
  initialSignature,
  onSave,
  onClear,
  width = 500,
  height = 200,
  label = "Signature",
  penColor = "#000000"
}) => {
  const sigCanvasRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const initialSignatureLoaded = useRef(false);

  // Initialize canvas dimensions on mount only
  useEffect(() => {
    if (sigCanvasRef.current) {
      const canvas = sigCanvasRef.current.getCanvas();
      const container = canvas.parentElement;
      const rect = container.getBoundingClientRect();

      // Set internal canvas dimensions to match display dimensions
      canvas.width = rect.width;
      canvas.height = height;

      // Get context and configure drawing settings
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 2;
    }
  }, []); // Only run on mount

  // Function to load a signature image
  const loadSignature = (signatureData) => {
    if (!sigCanvasRef.current) return;
    
    const img = new Image();
    img.onload = () => {
      const canvas = sigCanvasRef.current.getCanvas();
      // Clear the canvas first
      sigCanvasRef.current.clear();
      
      const ctx = canvas.getContext('2d');
      // Get actual canvas dimensions
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Scale image to fit canvas if needed
      let drawWidth, drawHeight;
      if (img.width > canvasWidth || img.height > canvasHeight) {
        const scaleX = canvasWidth / img.width;
        const scaleY = canvasHeight / img.height;
        const scale = Math.min(scaleX, scaleY);
        drawWidth = img.width * scale;
        drawHeight = img.height * scale;
      } else {
        drawWidth = img.width;
        drawHeight = img.height;
      }
      
      // Calculate position to center the image
      const x = (canvasWidth - drawWidth) / 2;
      const y = (canvasHeight - drawHeight) / 2;
      
      // Draw the image
      ctx.drawImage(img, x, y, drawWidth, drawHeight);
      setIsEmpty(false);
    };
    img.src = signatureData;
  };

  // Load initial signature when provided (only once)
  useEffect(() => {
    if (initialSignature && !initialSignatureLoaded.current) {
      loadSignature(initialSignature);
      initialSignatureLoaded.current = true;
    }
  }, [initialSignature]);

  const handleClear = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setIsEmpty(true);
      initialSignatureLoaded.current = false;
      if (onClear) onClear();
    }
  };

  const handleSave = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      // Get the signature as base64 string
      const signatureData = sigCanvasRef.current.toDataURL();
      if (onSave) onSave(signatureData);
    }
  };

  const handleBegin = () => {
    // Mark as not empty when user starts drawing
    setIsEmpty(false);
  };

  const handleEnd = () => {
    // Update empty state when user finishes drawing
    if (sigCanvasRef.current) {
      setIsEmpty(sigCanvasRef.current.isEmpty());
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="border rounded-md p-2 bg-white">
        <div style={{ width: '100%', height: `${height}px` }}>
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor={penColor}
            canvasProps={{
              className: 'signature-canvas',
              style: {
                width: '100%',
                height: '100%',
                touchAction: 'none' // Prevent scrolling while signing
              }
            }}
            onBegin={handleBegin}
            onEnd={handleEnd}
            clearOnResize={false}
          />
        </div>
      </div>
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={handleClear}
          className="btn-secondary flex-1"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn-primary flex-1"
          disabled={isEmpty}
        >
          Save Signature
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;